import { CalendarEvent, CalendarEventStatus } from './calendar-event.dto';

/**
 * Utilitaires pour la génération et l'interopérabilité des calendriers
 * selon les standards RFC 5545 (iCalendar), Google Calendar et CalDAV.
 */

/**
 * Formate une date ISO en chaîne compatible iCalendar (RFC 5545).
 * Format UTC : YYYYMMDDTHHmmssZ
 */
export function formatToICalDateTime(isoDate: string | Date, allDay = false): string {
  const d = typeof isoDate === 'string' ? new Date(isoDate) : isoDate;
  if (Number.isNaN(d.getTime())) {
    return '';
  }

  const pad = (n: number) => String(n).padStart(2, '0');
  const year = d.getUTCFullYear();
  const month = pad(d.getUTCMonth() + 1);
  const day = pad(d.getUTCDate());

  if (allDay) {
    return `${year}${month}${day}`;
  }

  const hours = pad(d.getUTCHours());
  const minutes = pad(d.getUTCMinutes());
  const seconds = pad(d.getUTCSeconds());

  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
}

/**
 * Échappe les caractères spéciaux pour le format texte iCalendar (RFC 5545).
 */
export function escapeICalText(text?: string): string {
  if (!text) return '';
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r\n|\r|\n/g, '\\n');
}

/**
 * Convertit un statut CalendarEventStatus en statut officiel RFC 5545.
 */
export function toRFC5545Status(status?: CalendarEventStatus): 'CONFIRMED' | 'TENTATIVE' | 'CANCELLED' {
  switch (status) {
    case 'confirmed':
    case 'completed':
      return 'CONFIRMED';
    case 'tentative':
      return 'TENTATIVE';
    case 'cancelled':
      return 'CANCELLED';
    default:
      return 'CONFIRMED';
  }
}

/**
 * Génère le contenu d'un fichier .ics (iCalendar VCALENDAR / VEVENT) conforme au standard RFC 5545.
 */
export function generateICalendar(
  events: CalendarEvent | CalendarEvent[],
  calendarName = 'ETXE Agenda',
): string {
  const eventList = Array.isArray(events) ? events : [events];
  const nowStr = formatToICalDateTime(new Date());

  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//ETXE//Agenda Calendar//FR',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${escapeICalText(calendarName)}`,
    'X-WR-TIMEZONE:Europe/Paris',
  ];

  for (const event of eventList) {
    const uid = event.syncInfo?.uid || `${event.id}@etxe.immo`;
    const dtStart = formatToICalDateTime(event.startAt, event.allDay);
    const dtEnd = formatToICalDateTime(event.endAt, event.allDay);
    const status = toRFC5545Status(event.status);

    lines.push('BEGIN:VEVENT');
    lines.push(`UID:${uid}`);
    lines.push(`DTSTAMP:${nowStr}`);

    if (event.allDay) {
      lines.push(`DTSTART;VALUE=DATE:${dtStart}`);
      lines.push(`DTEND;VALUE=DATE:${dtEnd}`);
    } else {
      lines.push(`DTSTART:${dtStart}`);
      lines.push(`DTEND:${dtEnd}`);
    }

    lines.push(`SUMMARY:${escapeICalText(event.title)}`);

    if (event.description) {
      lines.push(`DESCRIPTION:${escapeICalText(event.description)}`);
    }

    if (event.location?.address || event.location?.name) {
      const loc = [event.location.name, event.location.address].filter(Boolean).join(' - ');
      lines.push(`LOCATION:${escapeICalText(loc)}`);
    }

    lines.push(`STATUS:${status}`);

    if (event.syncInfo?.sequence !== undefined) {
      lines.push(`SEQUENCE:${event.syncInfo.sequence}`);
    }

    if (event.syncInfo?.recurrenceRule) {
      lines.push(`RRULE:${event.syncInfo.recurrenceRule}`);
    }

    if (event.url) {
      lines.push(`URL:${event.url}`);
    }

    if (event.attendees && event.attendees.length > 0) {
      for (const attendee of event.attendees) {
        if (attendee.email || attendee.name) {
          const cn = attendee.name ? `;CN=${escapeICalText(attendee.name)}` : '';
          const mail = attendee.email ? `mailto:${attendee.email}` : `mailto:user-${attendee.name}@etxe.immo`;
          lines.push(`ATTENDEE;ROLE=REQ-PARTICIPANT${cn}:${mail}`);
        }
      }
    }

    lines.push('END:VEVENT');
  }

  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}

/**
 * Construit un lien direct "Ajouter à Google Agenda".
 */
export function buildGoogleCalendarUrl(event: CalendarEvent): string {
  const baseUrl = 'https://calendar.google.com/calendar/render?action=TEMPLATE';

  const startFormatted = formatToICalDateTime(event.startAt, event.allDay);
  const endFormatted = formatToICalDateTime(event.endAt, event.allDay);
  const dates = `${startFormatted}/${endFormatted}`;

  const locationStr = [event.location?.name, event.location?.address].filter(Boolean).join(', ');

  const params = new URLSearchParams({
    text: event.title,
    dates,
    details: event.description || '',
    location: locationStr,
  });

  return `${baseUrl}&${params.toString()}`;
}

/**
 * Construit un lien direct "Ajouter à Outlook Web".
 */
export function buildOutlookCalendarUrl(event: CalendarEvent): string {
  const baseUrl =
    'https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent';

  const startFormatted = formatToICalDateTime(event.startAt, event.allDay);
  const endFormatted = formatToICalDateTime(event.endAt, event.allDay);

  const locationStr = [event.location?.name, event.location?.address].filter(Boolean).join(', ');

  const params = new URLSearchParams({
    subject: event.title,
    startdt: startFormatted,
    enddt: endFormatted,
    body: event.description || '',
    location: locationStr,
  });

  return `${baseUrl}&${params.toString()}`;
}
